/**
 * CulturalSelector - Select cultural/regional context for video generation
 *
 * Allows users to select a cultural context that influences:
 * - Visual aesthetics (landscapes, cityscapes, colors)
 * - Cultural references and icons
 * - Energy and vibe of the content
 */

import { useState, useEffect } from 'react';
import { Globe2, MapPin, Check } from 'lucide-react';

// Module-level guard that persists across component remounts
let culturesFetched = false;
let culturesFetchInProgress = false;
let cachedCultures: Culture[] = [];

interface Culture {
  code: string;
  name: string;
  region_type: string;
  vibe: string;
}

interface CulturalSelectorProps {
  selectedCulture: string | null;
  onSelect: (code: string | null) => void;
  className?: string;
}

// Organized by region for better UX
const CULTURE_GROUPS = {
  'North America': ['us-ny', 'us-ca', 'us-fl', 'us-tx'],
  'Europe': ['uk', 'de', 'it'],
  'Asia Pacific': ['au', 'jp', 'kr', 'in'],
  'Latin America': ['mx', 'br', 'jm'],
  'Africa': ['ng'],
};

const CULTURE_EMOJI: Record<string, string> = {
  'au': '🇦🇺',
  'us-fl': '🌴',
  'us-ny': '🗽',
  'us-ca': '☀️',
  'us-tx': '🤠',
  'uk': '🇬🇧',
  'mx': '🇲🇽',
  'jp': '🇯🇵',
  'br': '🇧🇷',
  'kr': '🇰🇷',
  'de': '🇩🇪',
  'in': '🇮🇳',
  'it': '🇮🇹',
  'ng': '🇳🇬',
  'jm': '🇯🇲',
};

export default function CulturalSelector({
  selectedCulture,
  onSelect,
  className = '',
}: CulturalSelectorProps) {
  const [cultures, setCultures] = useState<Culture[]>(cachedCultures);
  const [isLoading, setIsLoading] = useState(!culturesFetched);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    // Module-level guard: Only fetch once across all instances/remounts
    if (culturesFetched || culturesFetchInProgress) {
      console.log('[CulturalSelector] BLOCKED - already fetched or in progress');
      if (culturesFetched && cachedCultures.length > 0) {
        setCultures(cachedCultures);
        setIsLoading(false);
      }
      return;
    }

    culturesFetchInProgress = true;

    const fetchCultures = async () => {
      try {
        console.log('[CulturalSelector] Fetching cultures...');
        const apiUrl = import.meta.env.VITE_API_URL || '/api/v1';
        const response = await fetch(`${apiUrl}/cultures`);
        if (response.ok) {
          const data = await response.json();
          console.log('[CulturalSelector] Cultures loaded:', data.cultures?.length);
          cachedCultures = data.cultures || [];
          setCultures(cachedCultures);
          culturesFetched = true;
        }
      } catch (error) {
        console.error('Failed to fetch cultures:', error);
        // DON'T reset guard on error - prevents infinite retry loop
      } finally {
        culturesFetchInProgress = false;
        setIsLoading(false);
      }
    };

    fetchCultures();
  }, []);

  const getCultureByCode = (code: string) => cultures.find(c => c.code === code);
  const selectedCultureData = selectedCulture ? getCultureByCode(selectedCulture) : null;

  const getCulturesForGroup = (codes: string[]) => {
    return codes.map(code => getCultureByCode(code)).filter(Boolean) as Culture[];
  };

  if (isLoading) {
    return (
      <div className={`bg-zinc-800/50 rounded-xl p-4 ${className}`}>
        <div className="animate-pulse flex items-center gap-2">
          <div className="w-5 h-5 bg-zinc-700 rounded" />
          <div className="h-4 bg-zinc-700 rounded w-32" />
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-zinc-800/50 rounded-xl overflow-hidden ${className}`}>
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full p-4 flex items-center justify-between hover:bg-zinc-800/80 transition-colors"
      >
        <div className="flex items-center gap-3">
          <Globe2 className="w-5 h-5 text-purple-400" />
          <div className="text-left">
            <span className="text-sm font-medium text-zinc-200">
              Cultural Context
            </span>
            {selectedCultureData ? (
              <p className="text-xs text-purple-400 mt-0.5">
                {CULTURE_EMOJI[selectedCultureData.code] || '🌍'} {selectedCultureData.name}
              </p>
            ) : (
              <p className="text-xs text-zinc-500 mt-0.5">
                Optional - adds regional flavor
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {selectedCulture && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onSelect(null);
              }}
              className="text-xs text-zinc-500 hover:text-zinc-300 px-2 py-1 hover:bg-zinc-700 rounded"
            >
              Clear
            </button>
          )}
          <MapPin className={`w-4 h-4 text-zinc-500 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
        </div>
      </button>

      {/* Expanded selection */}
      {isExpanded && (
        <div className="border-t border-zinc-700/50 p-4 max-h-80 overflow-y-auto">
          {selectedCultureData && (
            <div className="mb-4 p-3 bg-purple-500/10 border border-purple-500/30 rounded-lg">
              <p className="text-xs text-purple-300 italic">
                "{selectedCultureData.vibe}"
              </p>
            </div>
          )}

          {Object.entries(CULTURE_GROUPS).map(([region, codes]) => {
            const regionCultures = getCulturesForGroup(codes);
            if (regionCultures.length === 0) return null;

            return (
              <div key={region} className="mb-4 last:mb-0">
                <h4 className="text-xs font-medium text-zinc-500 uppercase tracking-wider mb-2">
                  {region}
                </h4>
                <div className="grid grid-cols-2 gap-2">
                  {regionCultures.map((culture) => (
                    <button
                      key={culture.code}
                      onClick={() => {
                        onSelect(culture.code);
                        setIsExpanded(false);
                      }}
                      className={`
                        p-2 rounded-lg text-left transition-all
                        ${selectedCulture === culture.code
                          ? 'bg-purple-500/20 border border-purple-500/50'
                          : 'bg-zinc-700/30 border border-transparent hover:bg-zinc-700/50'
                        }
                      `}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-lg mr-2">
                          {CULTURE_EMOJI[culture.code] || '🌍'}
                        </span>
                        {selectedCulture === culture.code && (
                          <Check className="w-3 h-3 text-purple-400" />
                        )}
                      </div>
                      <p className="text-sm font-medium text-zinc-200 mt-1">
                        {culture.name}
                      </p>
                      <p className="text-xs text-zinc-500 capitalize">
                        {culture.region_type}
                      </p>
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
